<template>
  <div class="container-login w-full h-screen">
    <div
      v-if="isMobileDevice"
      class="w-full bg-[#FEF9E7] border-[#F1C21B] p-3 text-center text-sm text-textPrimary flex justify-center items-center gap-4 fixed top-0 z-50"
    >
      <span class="text-left inter">Para médicos, acesse o Minha Escala através do aplicativo</span>
      <button
        @click="openApp"
        class="bg-primary30 text-white font-bold py-1 px-4 rounded w-40"
        data-testid="recovery-open-app-button"
      >
        Abrir app
      </button>
      <button @click="isMobileDevice = false" class="font-bold text-lg">×</button>
    </div>
    <div class="logo flex justify-center">
      <Logo width="160px" logo="full_text_white" />
    </div>
    <div class="flex justify-center">
      <v-progress-circular
        v-if="isLoading"
        indeterminate
        :size="128"
        :width="7"
        color="primary"
      ></v-progress-circular>

      <div v-else class="card flex items-center flex-col rounded overflow-hidden shadow-lg gap-4">
        <InvalidTokenPage v-if="invalidToken" />
        <steps
          v-else
          @changeStep="changeStep"
          :stepSelected="stepSelected"
          :stepsLength="2"
          title="Redefinir senha"
          btnText="Alterar senha"
          :hiddenBtn="stepSelected === 1"
        >
          <template v-slot:step-0>
            <div class="flex flex-col gap-2 my-5 items-center">
              <h1 class="title">Criar nova senha de acesso</h1>
              <h5 class="subtitle">
                Preencha os campos abaixo para criar uma nova senha de acesso ao Minha Escala.
              </h5>
              <div class="flex flex-col gap-4 mt-5">
                <div data-testid="recovery-new-password-input">
                  <Input
                    v-model="form.password.value"
                    :isError="!!form.password.error"
                    :errorMessage="form.password.error"
                    width="329px"
                    label="Nova Senha"
                    type="password"
                    @blur="() => validate(form.password.validation, 'password')"
                  />
                </div>
                <div data-testid="recovery-repeat-password-input">
                  <Input
                    v-model="form.repeatPassword.value"
                    :isError="!!form.repeatPassword.error"
                    :errorMessage="form.repeatPassword.error"
                    width="329px"
                    label="Repetir nova senha"
                    type="password"
                    @blur="() => validate(form.repeatPassword.validation, 'repeatPassword')"
                  />
                </div>
              </div>
            </div>
          </template>
          <template v-slot:step-1>
            <div class="flex flex-col gap-2 my-5 items-center">
              <h1 class="title">Senha alterada com sucesso!</h1>
              <h5 class="subtitle">Você já pode acessar o painel Minha Escala com a nova senha.</h5>
              <FilledButton
                @click="goToLogin"
                text="Fazer login"
                width="329px"
                class="mt-5 bg-primary30"
                data-testid="recovery-login-button"
              />

              <p class="subtitle mt-4">É médico(a)? Acesse pelo aplicativo:</p>

              <AppStoreBadges class="mt-5" :android-url="androidUrl" :ios-url="iosUrl" />
            </div>
          </template>
        </steps>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as yup from 'yup'
import { ref, reactive, onBeforeMount, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import Logo from '@/components/designSystem/Logo/index.vue'
import Steps from '@/components/designSystem/Steps/Index.vue'
import InvalidTokenPage from './components/invalidTokenPage.vue'
import Network from '@/Network'
import Input from '@/components/designSystem/Inputs/Input.vue'
import { passwordValidator } from '@/hooks/useYup'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import AppStoreBadges from '@/components/designSystem/AppStoreBadges/index.vue'
import { isMobileUserAgent, openAppStore } from '@/utils/appStoreLinks'

const toast = useToast()
const androidUrl = ref('')
const iosUrl = ref('')
const token = ref('')
const invalidToken = ref(false)
const isLoading = ref(false)
const route = useRoute()
const router = useRouter()
const isMobileDevice = ref(false)

// Estrutura reativa para armazenar valores e erros
const form = reactive({
  password: {
    value: '',
    error: null,
    validation: passwordValidator
  },
  repeatPassword: {
    value: '',
    error: null,
    validation: yup.string().required('Repetir senha é obrigatório')
  }
})

const validate = (_validate, field) => {
  _validate
    .validate(form[field].value)
    .then(() => {
      form[field].error = null
    })
    .catch((err) => {
      form[field].error = err.message
    })
}

const validateForm = (): boolean => {
  let isValid = true

  const schema = yup.object().shape({
    password: passwordValidator,
    repeatPassword: yup
      .string()
      .oneOf([form.password.value], 'As senhas não coincidem')
      .required('Repetir senha é obrigatório')
  })

  try {
    schema.validateSync(
      {
        password: form.password.value,
        repeatPassword: form.repeatPassword.value
      },
      { abortEarly: false }
    )
    form.password.error = null
    form.repeatPassword.error = null
  } catch (err: any) {
    err.inner.forEach((validationError: any) => {
      form[validationError.path].error = validationError.message
      isValid = false
    })
  }

  return isValid
}

const detectDevice = () => {
  isMobileDevice.value = isMobileUserAgent()
}

const openApp = () => {
  openAppStore(() =>
    toast.info('Abra a loja de aplicativos do seu celular e procure por "Minha Escala"')
  )
}

onMounted(() => {
  detectDevice()
})

onBeforeMount(async () => {
  isLoading.value = true
  try {
    if (!route.query['token']) {
      invalidToken.value = true
      return
    }
    token.value = String(route.query['token'])
    await Network.user.validateToken({ token: token.value })
  } catch {
    toast.error('Token inválido ou expirado')
    token.value = ''
    invalidToken.value = true
  } finally {
    isLoading.value = false
  }
})

const stepSelected = ref(0)

async function changeStep(step: number) {
  if (step === 0) {
    if (!validateForm()) return
    try {
      const res = await Network.user.changePassword({
        password: form.password.value,
        repeatPassword: form.repeatPassword.value,
        token: token.value
      })
      androidUrl.value = res.androidUrl
      iosUrl.value = res.iosUrl
      stepSelected.value = 1
    } catch (error) {
      toast.error(
        'Aconteceu um erro ao tentar alterar a senha, repita o processo de recuperação de senha novamente!'
      )
    }
  }
}

const goToLogin = () => {
  router.push({ name: 'signin' })
}
</script>

<style scoped>
.container-login {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #2f7f91;
}
.card {
  width: 508px;
  padding: 3rem;
  background-color: #fff;
}

@media (max-width: 556px) {
  .card {
    width: 90%;
    background-color: #fff;
  }
}

.title {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
}
.subtitle {
  font-family: 'Inter';
  font-style: normal;
  max-width: 80%;
}
.input {
  margin: 3rem 0;
}
.logo {
  padding-bottom: 3rem;
}
</style>
